import { useState, useMemo } from 'react';
import { Loader2, Check, Send, Files, FileStack, AlertTriangle } from 'lucide-react';
import { ACCENT as SECTION_COLOR, OK, WARN, BAD } from '../../lib/uiTokens';

/**
 * Odeslání dokladů e-mailem — jeden doklad i celá dávka jednou zprávou.
 *
 * Posílá se ze schránky podatelny (lami@) jako od asistentky, takže adresát
 * může rovnou odpovědět tam, kde se pošta čte. V těle mailu jde s přílohami
 * i přehled dokladů, ať účetní vidí, co dostala, bez otevírání příloh.
 */

const LS_PRIJEMCI = 'doklady.mail.prijemci';

const fmt = (v) => (v == null ? '—' : new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v));
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('cs-CZ') : '—');
const jmenoDodavatele = (d) => d?.dodavatel?.nazev || (typeof d?.dodavatel === 'string' ? d.dodavatel : '');

const sklonuj = (n) => (n === 1 ? 'doklad' : n < 5 ? 'doklady' : 'dokladů');

/** Adresy oddělené čárkou/středníkem/mezerou — stejně je rozebere i backend. */
const rozeber = (s) => (s || '').split(/[,;\s]+/).filter(Boolean);
const platnaAdresa = (a) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a);

/** Naposledy použití příjemci — účetní se píše pořád dokola. */
function nactiPrijemce() {
    try {
        const ulozene = JSON.parse(localStorage.getItem(LS_PRIJEMCI) || '[]');
        return Array.isArray(ulozene) ? ulozene.filter((a) => typeof a === 'string').slice(0, 5) : [];
    } catch {
        return [];
    }
}

function zapamatujPrijemce(adresy) {
    try {
        const spojene = [...adresy, ...nactiPrijemce()];
        localStorage.setItem(LS_PRIJEMCI, JSON.stringify([...new Set(spojene)].slice(0, 5)));
    } catch {
        /* privátní režim / plné úložiště — pamatování je bonus, ne podmínka */
    }
}

function RezimTlacitko({ aktivni, onClick, Icon, popis, children }) {
    return (
        <button type="button" onClick={onClick} title={popis}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-semibold transition-colors"
            style={{
                background: aktivni ? `${SECTION_COLOR}18` : 'transparent',
                color: aktivni ? SECTION_COLOR : 'var(--text-tertiary)',
                border: `1px solid ${aktivni ? `${SECTION_COLOR}66` : 'var(--border-default)'}`,
            }}>
            <Icon size={14} />
            {children}
        </button>
    );
}

export default function OdeslatMailemModal({ doklady, cardStyle, onClose, onOdeslano }) {
    const seznam = useMemo(() => (Array.isArray(doklady) ? doklady : [doklady]).filter(Boolean), [doklady]);
    const davka = seznam.length > 1;

    const [prijemci, setPrijemci] = useState('');
    const [zprava, setZprava] = useState('');
    // Dávka jde ve sloučeném PDF (jedna příloha, vejde se do limitu pošty),
    // jeden doklad naopak v originále — nemá smysl ho překlápět a zhoršovat.
    const [rezim, setRezim] = useState(davka ? 'slouceno' : 'jednotlive');
    const [stav, setStav] = useState(null);      // null | 'odesila' | 'ok'
    const [chyba, setChyba] = useState(null);
    const [preskoceno, setPreskoceno] = useState([]);

    const znameAdresy = useMemo(nactiPrijemce, []);
    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    const adresy = rozeber(prijemci);
    const platne = adresy.length > 0 && adresy.every(platnaAdresa);
    const suma = seznam.reduce((s, d) => s + (Number(d.castka_s_dph) || 0), 0);

    const odeslat = async () => {
        setStav('odesila');
        setChyba(null);
        setPreskoceno([]);
        try {
            const r = await fetch('/api/finance/doklady/odeslat-mailem', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({
                    ids: seznam.map((d) => d.doklad_id ?? d.id),
                    prijemci: adresy.join(', '),
                    zprava: zprava || null,
                    rezim,
                }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setChyba(data.error || 'Odeslání selhalo.');
                setPreskoceno(data.preskoceno || []);
                setStav(null);
                return;
            }
            zapamatujPrijemce(adresy);
            setPreskoceno(data.preskoceno || []);
            setStav('ok');
            onOdeslano?.(data);
            // Když se něco nepřiložilo, nechej okno otevřené — ať to uživatel vidí.
            if (!data.preskoceno?.length) setTimeout(onClose, 1400);
        } catch {
            setChyba('Síťová chyba při odesílání.');
            setStav(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="w-full max-w-lg rounded-2xl p-5 max-h-[90vh] overflow-y-auto" style={cardStyle} onClick={(e) => e.stopPropagation()}>
                <h3 className="text-base font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
                    {davka ? `Odeslat ${seznam.length} ${sklonuj(seznam.length)} e-mailem` : 'Odeslat doklad e-mailem'}
                </h3>
                <p className="text-xs mb-4" style={{ color: 'var(--text-tertiary)' }}>
                    Odejde z <strong>lami@fofrlama.cz</strong> jako od asistentky — adresát může rovnou odpovědět.
                    {davka && ' Vše přijde v jednom mailu i s přehledovou tabulkou.'}
                </p>

                {stav === 'ok' ? (
                    <div className="py-6 text-center">
                        <div className="text-sm font-semibold" style={{ color: OK }}>
                            <Check size={20} className="inline mr-1" /> Odesláno na {adresy.join(', ')}
                        </div>
                        {preskoceno.length > 0 && (
                            <ul className="mt-3 text-left text-xs list-disc list-inside" style={{ color: WARN }}>
                                {preskoceno.map((p, i) => <li key={i}>{p}</li>)}
                            </ul>
                        )}
                        {preskoceno.length > 0 && (
                            <button onClick={onClose} className="mt-4 px-4 py-2 rounded-lg text-sm font-semibold"
                                style={{ color: 'var(--text-secondary)' }}>Zavřít</button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Co se posílá — ať je vidět, že výběr sedí */}
                        <div className="rounded-lg mb-3 overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
                            <div className="max-h-40 overflow-y-auto">
                                {seznam.map((d) => (
                                    <div key={d.doklad_id ?? d.id} className="flex items-center gap-2 px-2.5 py-1.5 text-xs"
                                        style={{ borderBottom: '1px solid var(--border-subtle, var(--border-default))' }}>
                                        <span className="font-mono font-semibold flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>
                                            {d.interni_cislo || '—'}
                                        </span>
                                        <span className="truncate flex-1" style={{ color: 'var(--text-primary)' }}>
                                            {jmenoDodavatele(d) || <span className="italic" style={{ color: 'var(--text-tertiary)' }}>nespárován</span>}
                                        </span>
                                        <span className="flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                                            {fmtDate(d.datum_zdan_plneni || d.datum_vystaveni || d.datum)}
                                        </span>
                                        <span className="tabular-nums font-semibold flex-shrink-0" style={{ color: 'var(--text-primary)' }}>
                                            {fmt(d.castka_s_dph)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            {davka && (
                                <div className="flex items-center justify-between px-2.5 py-1.5 text-xs font-bold"
                                    style={{ background: `${SECTION_COLOR}0e`, color: 'var(--text-primary)' }}>
                                    <span>Celkem {seznam.length} {sklonuj(seznam.length)}</span>
                                    <span className="tabular-nums">{fmt(suma)} Kč</span>
                                </div>
                            )}
                        </div>

                        <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
                            E-mail příjemce <span style={{ color: 'var(--text-tertiary)' }}>(víc adres oddělte čárkou)</span>
                        </label>
                        <input
                            type="text" value={prijemci} onChange={(e) => setPrijemci(e.target.value)}
                            placeholder="ucetni@example.cz" autoFocus list="doklady-mail-prijemci"
                            className="w-full rounded-lg px-3 py-2 text-sm mb-3"
                            style={{ ...cardStyle, color: 'var(--text-primary)' }}
                        />
                        <datalist id="doklady-mail-prijemci">
                            {znameAdresy.map((a) => <option key={a} value={a} />)}
                        </datalist>

                        <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Přílohy</label>
                        <div className="flex gap-2 mb-3">
                            <RezimTlacitko aktivni={rezim === 'slouceno'} onClick={() => setRezim('slouceno')} Icon={FileStack}
                                popis="Vše slité do jednoho komprimovaného PDF — jedna příloha, menší mail">
                                Jedno sloučené PDF
                            </RezimTlacitko>
                            <RezimTlacitko aktivni={rezim === 'jednotlive'} onClick={() => setRezim('jednotlive')} Icon={Files}
                                popis="Soubor na doklad v původní kvalitě, pojmenovaný podle interního čísla">
                                Každý zvlášť
                            </RezimTlacitko>
                        </div>

                        <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Zpráva (nepovinné)</label>
                        <textarea
                            value={zprava} onChange={(e) => setZprava(e.target.value)} rows={3} maxLength={2000}
                            placeholder={davka ? 'Dobrý den, posílám doklady za srpen…' : 'Dobrý den, posílám doklad…'}
                            className="w-full rounded-lg px-3 py-2 text-sm mb-3 resize-none"
                            style={{ ...cardStyle, color: 'var(--text-primary)' }}
                        />

                        {chyba && (
                            <div className="text-xs mb-3 px-3 py-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.10)', color: BAD }}>
                                {chyba}
                            </div>
                        )}
                        {preskoceno.length > 0 && (
                            <div className="text-xs mb-3 px-3 py-2 rounded-lg flex gap-2" style={{ background: `${WARN}14`, color: WARN }}>
                                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                                <ul className="list-disc list-inside">
                                    {preskoceno.map((p, i) => <li key={i}>{p}</li>)}
                                </ul>
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm font-medium"
                                style={{ color: 'var(--text-tertiary)' }}>Zrušit</button>
                            <button onClick={odeslat} disabled={!platne || stav === 'odesila'}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                                style={{ background: SECTION_COLOR, opacity: !platne || stav === 'odesila' ? 0.5 : 1 }}>
                                {stav === 'odesila' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                Odeslat
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
